/**
 * LA VENTANA DE ATENCIÓN DE 24 h DE META — la regla pura, en un solo sitio.
 *
 * ⚠️ COPIA BYTE-IDÉNTICA en `src/utils/ventanaDeAtencion.js`. Cualquier cambio va en
 * las dos, y `functions/scripts/check-shared-module-parity.js` bloquea el commit si
 * divergen. `src/` no puede importar de `functions/`, y esta regla la necesitan los
 * dos lados: el backend para decidir si envía, la UI para avisar antes de que el
 * operador escriba.
 *
 * Aquí NO hay nada de Firestore a propósito. Leer el historial es otra
 * responsabilidad y vive en `functions/bot/utils/ventanaAtencion.js`, que es lo único
 * que el navegador no podría ejecutar.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ⚠️ EL PARÁMETRO ES UN UMBRAL, NO UN MARGEN. LÉASE ESTO ANTES DE TOCAR UN NÚMERO.
 * ───────────────────────────────────────────────────────────────────────────────
 * `requierePlantilla(ultimo, ahora, umbralMs)` devuelve true cuando
 * **han pasado MÁS de `umbralMs` desde el último mensaje del cliente**. O sea:
 * `umbralMs` es el TIEMPO QUE PUEDE TRANSCURRIR ANTES DE NECESITAR PLANTILLA, no
 * el colchón que se le resta a las 24 h de Meta.
 *
 * Las constantes se llamaban `MARGEN_*` y eso costó una regresión en producción el
 * 21-sep-2026. Al añadir el camino del agente se pidió «un margen del orden de
 * minutos» y se escribió `MARGEN_AGENTE_MS = 5 minutos`, leyéndolo como «cinco
 * minutos de colchón». La función lo leyó como «plantilla a partir de los cinco
 * minutos», que es lo que significa:
 *
 *     hace  0,5 h -> true      hace 2,6 h -> true      hace 12 h -> true
 *
 * Resultado medido: **los 233 chats de WhatsApp Cloud enseñaban «esta conversación
 * expiró»**, y el 403 preventivo habría rebotado cualquier respuesta de agente
 * enviada más de cinco minutos después del cliente. No rebotó ninguna por tres
 * minutos de suerte: el único envío tras el despliegue salió a los 3,2 minutos.
 *
 * Por eso ahora se llaman `UMBRAL_*` y por eso el valor del agente se escribe como
 * una RESTA EXPLÍCITA de las 24 h de Meta, para que el colchón se vea y no se
 * confunda con el umbral.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * UNA REGLA, DOS UMBRALES — y el colchón NO es un detalle de afinado
 * ───────────────────────────────────────────────────────────────────────────────
 * El límite de Meta son 24 h desde el último mensaje del cliente, pero **el borde lo
 * evalúa su reloj, no el nuestro**. El colchón cubre ese desfase. Cuánto conviene
 * depende por completo de QUÉ CUESTA EQUIVOCARSE, y eso difiere en cada camino:
 *
 * **Recordatorios — colchón de 1 h, umbral 23 h.** Un falso positivo manda la
 * plantilla `appointment_reminder`, que es exactamente el mensaje que se quería
 * enviar: coste cero. Y el job corre cada hora, así que entre el cálculo y el envío
 * puede pasar una pasada entera; una hora es además su granularidad natural.
 *
 * **Agente humano — colchón de 5 min, umbral 23 h 55 min.** Aquí el intercambio se
 * invierte:
 *
 *   · NO HAY PLANTILLA DE RESPALDO que diga lo que el operador quería decir. La
 *     única UTILITY aprobada es un recordatorio de cita. Un falso positivo lo deja
 *     sin forma de contestar **con la ventana todavía abierta**.
 *   · El cálculo y el envío están a SEGUNDOS: la comprobación corre justo antes del
 *     POST, no una pasada de job después.
 *
 * Cinco minutos es el 0,35 % de la ventana: cubre el desfase de relojes y el viaje de
 * ida y vuelta con dos órdenes de magnitud de holgura, y acota el falso positivo a
 * los últimos cinco minutos de un día entero — donde además el mensaje bloqueado es
 * el que más probabilidad tenía de cruzar al otro lado en el trayecto.
 *
 * **NO UNIFIQUES LOS DOS UMBRALES.** Son la misma regla con dos colchones, cada uno
 * justificado por el coste de su propio falso positivo.
 */

/** Los 24 h de Meta. Ni umbral ni colchón: el límite del que salen los dos. */
const VENTANA_META_MS = 24 * 60 * 60 * 1000;

/**
 * Recordatorios programados: plantilla a partir de las 23 h.
 * Falso positivo barato — manda la plantilla que tocaba.
 */
const UMBRAL_RECORDATORIO_MS = VENTANA_META_MS - 60 * 60 * 1000;   // 24 h − 1 h = 23 h

/**
 * Envío manual del agente: plantilla a partir de las 23 h 55 min.
 * Falso positivo caro — no hay plantilla que sustituya lo que quería decir.
 */
const UMBRAL_AGENTE_MS = VENTANA_META_MS - 5 * 60 * 1000;          // 24 h − 5 min

/**
 * ¿Hay que mandar plantilla en vez de texto libre?
 *
 * @param {Date|null} ultimoDeCliente - instante del último mensaje DEL CLIENTE, o null
 *   si no hay ninguno.
 * @param {Date} [ahora]
 * @param {number} [umbralMs] - ⚠️ UMBRAL, no margen: el tiempo que puede pasar ANTES
 *   de necesitar plantilla. Uno de los dos de arriba. El defecto es el de
 *   recordatorios porque es el llamante que existía cuando esto se extrajo; quien
 *   añada un camino nuevo tiene que ELEGIR el suyo, no heredarlo por descuido.
 * @returns {boolean} true -> plantilla. false -> texto libre.
 *
 * SIN MENSAJES DE CLIENTE -> PLANTILLA. Es la dirección segura: un chat sin ningún
 * mensaje entrante no tiene ventana abierta que aprovechar, y el texto libre sería
 * rechazado. Ante la duda, plantilla — que llega siempre aunque sea más rígida.
 */
function requierePlantilla(ultimoDeCliente, ahora = new Date(), umbralMs = UMBRAL_RECORDATORIO_MS) {
  if (!(ultimoDeCliente instanceof Date) || Number.isNaN(ultimoDeCliente.getTime())) return true;
  return (ahora.getTime() - ultimoDeCliente.getTime()) >= umbralMs;
}

module.exports = {
  VENTANA_META_MS,
  UMBRAL_RECORDATORIO_MS,
  UMBRAL_AGENTE_MS,
  requierePlantilla,
};
